import React, { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

/**
 * Side-by-side strategy comparison component
 * Provides comparative analysis and statistical significance testing
 */
const StrategyComparison = ({ 
  strategies = [], 
  comparisonMetrics = [], 
  onExportComparison 
}) => {
  const [selectedView, setSelectedView] = useState('metrics');
  const [sortConfig, setSortConfig] = useState({ key: 'roi', direction: 'desc' });

  const sortedStrategies = useMemo(() => {
    if (!strategies.length) return [];
    
    return [...strategies].sort((a, b) => {
      const aValue = a.performance[sortConfig.key] || 0;
      const bValue = b.performance[sortConfig.key] || 0;
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [strategies, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getPerformanceBadge = (value, type = 'percentage') => {
    const isPositive = value > 0;
    const isNeutral = value === 0;
    
    return (
      <Badge 
        variant={isPositive ? 'default' : isNeutral ? 'secondary' : 'destructive'}
        className="text-xs"
      >
        {type === 'percentage' ? formatPercentage(value) : formatCurrency(value)}
      </Badge>
    );
  };

  if (!strategies.length) {
    return (
      <Card className="p-8 text-center">
        <div className="text-gray-500">
          <div className="text-lg font-medium mb-2">No strategies to compare</div>
          <div className="text-sm">Add multiple strategies to enable comparison analysis</div>
        </div>
      </Card>
    );
  }

  if (strategies.length < 2) {
    return (
      <Card className="p-8 text-center">
        <div className="text-gray-500">
          <div className="text-lg font-medium mb-2">Need at least 2 strategies</div>
          <div className="text-sm">Add another strategy to enable comparison analysis</div>
        </div>
      </Card>
    );
  }

  return (
    <div className="strategy-comparison space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Strategy Comparison</h3>
          <p className="text-sm text-gray-600">
            Comparing {strategies.length} strategies across key performance metrics
          </p>
        </div>
        <Button onClick={onExportComparison} size="sm" variant="outline">
          Export Comparison
        </Button>
      </div>

      <Tabs value={selectedView} onValueChange={setSelectedView}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="metrics">Performance Metrics</TabsTrigger>
          <TabsTrigger value="charts">Visual Comparison</TabsTrigger>
          <TabsTrigger value="statistical">Statistical Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="mt-4">
          <Card className="p-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Strategy</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('netProfit')}
                    >
                      Total Profit {sortConfig.key === 'netProfit' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('roi')}
                    >
                      ROI {sortConfig.key === 'roi' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('winRate')}
                    >
                      Win Rate {sortConfig.key === 'winRate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('maxDrawdown')}
                    >
                      Max Drawdown {sortConfig.key === 'maxDrawdown' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('totalBets')}
                    >
                      Total Bets {sortConfig.key === 'totalBets' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('sharpeRatio')}
                    >
                      Sharpe Ratio {sortConfig.key === 'sharpeRatio' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedStrategies.map((strategy, index) => (
                    <TableRow key={strategy.strategy.id}>
                      <TableCell className="font-medium">
                        <div>
                          <div className="font-semibold">{strategy.strategy.name}</div>
                          <div className="text-xs text-gray-500">{strategy.strategy.description}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getPerformanceBadge(strategy.performance.netProfit, 'currency')}
                      </TableCell>
                      <TableCell>
                        {getPerformanceBadge(strategy.performance.roi)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {formatPercentage(strategy.performance.winRate)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="destructive" className="text-xs">
                          {formatCurrency(Math.abs(strategy.performance.maxDrawdown))}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {strategy.performance.totalBets.toLocaleString()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={strategy.performance.sharpeRatio > 1 ? 'default' : 'secondary'} 
                          className="text-xs"
                        >
                          {strategy.performance.sharpeRatio?.toFixed(2) || 'N/A'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="charts" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4">
              <h4 className="font-semibold mb-4">Cumulative Performance</h4>
              <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">Comparative P&L Chart</div>
                  <div className="text-sm">Multi-strategy performance overlay</div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <h4 className="font-semibold mb-4">Risk vs Return</h4>
              <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">Scatter Plot</div>
                  <div className="text-sm">ROI vs Max Drawdown</div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <h4 className="font-semibold mb-4">Performance Distribution</h4>
              <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">Box Plot</div>
                  <div className="text-sm">Strategy performance distribution</div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <h4 className="font-semibold mb-4">Rolling Performance</h4>
              <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">Rolling ROI Chart</div>
                  <div className="text-sm">30-day rolling performance</div>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="statistical" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4">
              <h4 className="font-semibold mb-4">Statistical Significance</h4>
              <div className="space-y-3">
                {comparisonMetrics.map((metric, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <div className="text-sm font-medium">{metric.name}</div>
                    <div className="flex items-center space-x-2">
                      <Badge 
                        variant={Math.abs(metric.percentageDifference) > 10 ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {metric.percentageDifference > 0 ? '+' : ''}{metric.percentageDifference.toFixed(1)}%
                      </Badge>
                      {metric.significance && (
                        <Badge 
                          variant={metric.significance < 0.05 ? 'default' : 'outline'}
                          className="text-xs"
                        >
                          p={metric.significance.toFixed(3)}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-4">
              <h4 className="font-semibold mb-4">Risk Metrics</h4>
              <div className="space-y-3">
                <div className="text-sm text-gray-600">
                  Detailed risk analysis and correlation metrics will be implemented here
                </div>
                <div className="h-32 flex items-center justify-center border-2 border-dashed border-gray-200 rounded">
                  <div className="text-center text-gray-500">
                    <div className="text-sm">Risk Analysis Table</div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StrategyComparison;